import React, { useState, useEffect } from "react";
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import {
  Tooltip,
  IconButton,
  ClickAwayListener,
  tooltipClasses,
  TooltipProps,
  Modal,
  Box,
  SxProps,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "../../firebase/firebase";

const closeButtonStyles: SxProps = {
  position: "fixed",
  right: "12%",
  "&:hover": {
    backgroundColor: "lightgray",
  },
};

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 460,
  },
});

const InfoButton: React.FC<{
  windowName: string;
}> = ({ windowName }) => {
  const [TooltipOpen, setTooltipOpen] = useState(false); 
  const [imgFileList, setImgFileList] = useState<string[]>([]);
  const slideNum = 28;

  const fetchImages = async () => {
    // 画像取得処理を関数化
    try {
      const imgFileNames = Array.from(
        { length: slideNum },
        (_, i) => `manual${i + 1}.webp`
      );
      const folderPath = "AffiliationGroup/manual/";

      const urls = await Promise.all(
        imgFileNames.map(async (fileName) => {
          const fullPath = `${folderPath}${fileName}`;
          const fileRef = ref(storage, fullPath);
          const url = await getDownloadURL(fileRef);
          return url;
        })
      );
      setImgFileList(urls);
    } catch (error) {
      console.error("Error getting file URLs: ", error);
    }
  };

  useEffect(() => {
    if (windowName === "品揃え分析（新上位100単品）") {
      fetchImages(); // 画像取得処理を実行
    }
  }, [windowName]);

  const toolTheme = createTheme({
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            textAlign: "left",
            width: "460px",
            margin: 0,
          },
        },
      },
    },
  });

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
  };

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  const titleContent = (() => {
    if (windowName === "日別管理表") {
      return (
        <span style={{ width: "350px", fontSize: "12px" }}>
          ・商品売上高および客数は売上日報締め時点、
          <br />
          　商品廃棄は在庫変更報告書締め時点、
          <br />
          　客単価、買上点数、朝/昼/夜帯売上は0-24時を
          <br />
          　1日として表示しています。
          <br />
          ・前日までに承認した売上日報、在庫変更報告書が
          <br />
          　反映されます。
        </span>
      );
    } else if (windowName === "廃棄検証（PMA）") {
      return (
        <span style={{ width: "350px", fontSize: "12px" }}>
          ・在庫変更報告書を参考としているため、
          <br />
          　「商品廃棄」と各PMAの合計値は一致しない場合があります。
          <br />
          ・在庫変更報告書の承認済みデータを参考としているため、
          <br />
          　承認のタイミングによっては正しくデータが表示されない場合があります。
        </span>
      );
    } else if (
      windowName === "POS-PMA分析_PMA" ||
      windowName === "POS-PMA分析_サマリ" ||
      windowName.includes("情報分類")
    ) {
      return (
        <span style={{ width: "460px", fontSize: "12px" }}>
          ・PMA「クーポン」、「消耗品」、「消耗品（本部）」、
          <br />
          　「試食、試飲、展示」は表示しておりません。
          <br />
          ・PMA「サービス商品」には「コピー・プリント」が含まれておりません。
          <br />
          ・情報分類「コピー・プリント」は表示されておりません。
          <br />
          ・自店の期間平均の値は、以下理由により本部情報分析の値と一致いたしません。
          <br />
          ※本部情報分析は休業日を含む、OFCダッシュボードは休業日を除く。
        </span>
      );
    } else if (windowName === "品揃え分析（新上位100単品）") {
      return (
        <div style={{ fontSize: "12px" }}>
          <IconButton
            sx={closeButtonStyles}
            onClick={handleTooltipClose}
            size={"small"}
          >
            <CloseIcon />
          </IconButton>
          {imgFileList.length > 0 &&
            imgFileList.map((res, index) => (
              <div key={index} style={{ marginBottom: "8px" }}>
                <img
                  alt=""
                  src={res}
                  width="100%"
                  style={{ display: "block" }}
                />
              </div>
            ))}
        </div>
      );
    }
    return null;
  })();

  if (!titleContent) {
    return null;
  }

  if (windowName === "品揃え分析（新上位100単品）") {
    return (
      <div style={{ textAlign: "center" }}>
        <Tooltip
          title={<span style={{ fontSize: "16px", whiteSpace: "nowrap" }}>利用マニュアルを参照する</span>}
          placement="left"
          disableTouchListener
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: "none",
                padding: "6px 12px",
                width: "fit-content"
              }
            }
          }}
        >
          <IconButton
            onClick={handleTooltipOpen}
            style={{ padding: "2px 8px" }}
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Modal open={TooltipOpen} onClose={handleTooltipClose} style={{ height: "95vh" }}>
          <Box
            sx={{
              position: "absolute",
              top: "5%",
              right: "10%",
              width: "75%",
              height: "95%",
              bgcolor: "background.paper",
              border: "2px solid #000",
              boxShadow: 24,
              p: "8px 16px 8px",
              overflowY: "scroll",
              "&:focus": {
                outline: "none",
              },
            }}
          >
            {titleContent}
          </Box>
        </Modal>
      </div>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <div style={{ textAlign: "right" }}>
        <ThemeProvider theme={toolTheme}>
          <CustomWidthTooltip
            PopperProps={{
              disablePortal: true,
              placement: "left",
            }}
            onClose={handleTooltipClose}
            open={TooltipOpen}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={titleContent}
          >
            <IconButton
              onClick={handleTooltipOpen}
              style={{ padding: "2px 8px" }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </CustomWidthTooltip>
        </ThemeProvider>
      </div>
    </ClickAwayListener>
  );
};

export default InfoButton;
